import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { complaintAPI, customerAPI, userAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { Plus, Search, MessageSquare, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const PRIORITY_BADGE = { low: 'info', medium: 'pending', high: 'warning', critical: 'danger' };
const STATUS_BADGE = { open: 'open', in_progress: 'in_progress', resolved: 'resolved', closed: 'closed' };

function NewComplaintModal({ onClose, onSuccess }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    customer: '', category: 'no_internet', title: '', description: '',
    priority: 'medium', serviceType: 'broadband'
  });
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (search.length >= 2) {
      customerAPI.getAll({ search, limit: 10 })
        .then(({ data }) => setCustomers(data.data))
        .catch(() => {});
    }
  }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await complaintAPI.create(form);
      toast.success(`Ticket ${data.data.ticketNumber} created`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>New Support Ticket</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Customer <span className="required">*</span></label>
              <input className="form-control" placeholder="Search customer by name / mobile..."
                value={search} onChange={e => setSearch(e.target.value)} />
              {customers.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 6, marginTop: 4, maxHeight: 150, overflowY: 'auto' }}>
                  {customers.map(c => (
                    <div key={c._id} onClick={() => { setForm(f => ({ ...f, customer: c._id })); setSearch(`${c.name} (${c.customerId})`); setCustomers([]); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '.85rem', borderBottom: '1px solid var(--border)' }}
                      onMouseOver={e => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseOut={e => e.currentTarget.style.background = 'white'}>
                      <strong>{c.name}</strong> · {c.customerId} · {c.mobile}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="form-control" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  <option value="no_internet">No Internet</option>
                  <option value="slow_internet">Slow Internet</option>
                  <option value="intermittent_connection">Intermittent Connection</option>
                  <option value="no_tv_signal">No TV Signal</option>
                  <option value="poor_tv_quality">Poor TV Quality</option>
                  <option value="missing_channels">Missing Channels</option>
                  <option value="billing_dispute">Billing Dispute</option>
                  <option value="plan_change">Plan Change</option>
                  <option value="device_issue">Device Issue</option>
                  <option value="installation_request">Installation Request</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Priority</label>
                <select className="form-control" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Title <span className="required">*</span></label>
              <input className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Brief description of the issue" required />
            </div>
            <div className="form-group">
              <label className="form-label">Description <span className="required">*</span></label>
              <textarea className="form-control" rows={4} value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Detailed description..." required />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading || !form.customer}>
              <MessageSquare size={14} /> {loading ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ComplaintsPage() {
  const { user } = useAuth();
  const [complaints, setComplaints] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({ page: 1, status: '', priority: '', category: '' });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, limit: 20 };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const [cd, sd] = await Promise.all([
        complaintAPI.getAll(params),
        complaintAPI.getStats()
      ]);
      setComplaints(cd.data.data);
      setPagination(cd.data.pagination);
      setStats(sd.data.data);
    } catch { toast.error('Failed to load tickets'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Support Tickets</h2>
          <p className="text-muted text-small">{pagination.total || 0} total tickets</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={15} /> New Ticket
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stats-grid mb-6" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          {[
            { label: 'Open', value: stats.open, color: '#dc2626', bg: '#fee2e2' },
            { label: 'In Progress', value: stats.inProgress, color: '#d97706', bg: '#fef3c7' },
            { label: 'SLA Breach', value: stats.overdueSLA, color: '#7c3aed', bg: '#ede9fe' },
            { label: 'Resolved', value: stats.resolved, color: '#059669', bg: '#d1fae5' },
          ].map(s => (
            <div key={s.label} className="stat-card">
              <div className="stat-icon" style={{ background: s.bg }}>
                <AlertCircle size={20} color={s.color} />
              </div>
              <div className="stat-info"><p>{s.label}</p><h3>{s.value}</h3></div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="filter-bar">
        <select className="form-control" style={{ width: 140 }} value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </select>
        <select className="form-control" style={{ width: 130 }} value={filters.priority}
          onChange={e => setFilters(f => ({ ...f, priority: e.target.value, page: 1 }))}>
          <option value="">All Priority</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <select className="form-control" style={{ width: 180 }} value={filters.category}
          onChange={e => setFilters(f => ({ ...f, category: e.target.value, page: 1 }))}>
          <option value="">All Categories</option>
          <option value="no_internet">No Internet</option>
          <option value="slow_internet">Slow Internet</option>
          <option value="no_tv_signal">No TV Signal</option>
          <option value="billing_dispute">Billing</option>
          <option value="device_issue">Device Issue</option>
        </select>
      </div>

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : complaints.length === 0 ? (
            <div className="empty-state"><MessageSquare size={32} /><h3>No tickets found</h3></div>
          ) : (
            <table>
              <thead>
                <tr><th>Ticket</th><th>Customer</th><th>Category</th><th>Priority</th><th>Status</th><th>Assigned</th><th>Created</th><th></th></tr>
              </thead>
              <tbody>
                {complaints.map(c => (
                  <tr key={c._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/complaints/${c._id}`)}>
                    <td className="font-mono" style={{ fontSize: '.78rem' }}>{c.ticketNumber}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.customer?.name}</div>
                      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{c.customer?.mobile}</div>
                    </td>
                    <td style={{ fontSize: '.82rem', textTransform: 'capitalize' }}>{c.category?.replace(/_/g, ' ')}</td>
                    <td>
                      <span className={`badge badge-${PRIORITY_BADGE[c.priority]}`} style={{ textTransform: 'capitalize' }}>
                        {c.priority}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${STATUS_BADGE[c.status]}`} style={{ textTransform: 'capitalize' }}>
                        {c.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td style={{ fontSize: '.82rem' }}>{c.assignedTo?.name || <span style={{ color: 'var(--text-light)' }}>Unassigned</span>}</td>
                    <td style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{format(new Date(c.createdAt), 'dd/MM/yy HH:mm')}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <Link to={`/complaints/${c._id}`} className="btn btn-secondary btn-sm">View</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {pagination.pages > 1 && (
          <div className="pagination">
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <span style={{ fontSize: '.8rem', padding: '0 8px' }}>{pagination.page} / {pagination.pages}</span>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {showModal && <NewComplaintModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); load(); }} />}
    </div>
  );
}
